import URLS from '@/store/urls';
import apiCall from '@/store/apiCall';
import notificationWithIcon from '@/utils/notification';

const actions = {
  async getCompanies({ commit }, { limit = 10, offset = 0 } = {}) {
    commit('GET_COMPANIES_REQUEST');
    try {
      const { data: { data } } = await apiCall({
        type: 'GET',
        url: `${URLS.companies}?pagination[limit]=${limit}&pagination[offset]=${offset}`,
        isToken: true,
      });
      commit('GET_COMPANIES_SUCCESS', data);
    } catch (err) {
      commit('GET_COMPANIES_ERROR');
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async getCompany({ commit }, id) {
    commit('GET_COMPANY_REQUEST');
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companies}/${id}`,
        isToken: true,
      });
      commit('GET_COMPANY_SUCCESS', data.data);
      return data.data;
    } catch (err) {
      commit('GET_COMPANY_ERROR');
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },

  async createCompany({ dispatch }, { values, limit, offset }) {
    try {
      await apiCall({
        type: 'POST',
        url: URLS.companies,
        body: values,
        isToken: true,
      });
      notificationWithIcon('success', 'Company created successfully');
      dispatch('getCompanies', { limit, offset });
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },

  async updateCompany({ dispatch }, {
    id, values, limit, offset,
  }) {
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.companies}/${id}`,
        body: values,
        isToken: true,
      });
      notificationWithIcon('success', 'Company updated successfully');
      dispatch('getCompanies', { limit, offset });
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },

  async deleteCompany({ dispatch }, { id, limit, offset }) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.companies}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Company deleted successfully');
      dispatch('getCompanies', { limit, offset });
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
    }
  },

  async getCompanyColors({ commit }, companyId) {
    try {
      const { data } = await apiCall({
        type: 'GET',
        url: `${URLS.companyColor}/company/${companyId}`,
        isToken: true,
      });
      commit('GET_COMPANY_COLORS_SUCCESS', data.data);
      return data.data;
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },
  async updateCompanyColor(_, { id, ...values }) {
    try {
      await apiCall({
        type: 'PUT',
        url: `${URLS.companyColor}/${id}`,
        body: values,
        isToken: true,
      });
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },
  async createCompanyColors(_, { companyId, colors }) {
    try {
      await Promise.all(colors.map((color) => apiCall({
        type: 'POST',
        url: URLS.companyColor,
        body: {
          ...color,
          companyId,
        },
        isToken: true,
      })));
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },
  async deleteCompanyColor({ dispatch }, { id, companyId }) {
    try {
      await apiCall({
        type: 'DELETE',
        url: `${URLS.companyColor}/${id}`,
        isToken: true,
      });
      notificationWithIcon('success', 'Company color deleted successfully');
      dispatch('getCompanyColors', companyId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },

  async saveCompanyColors({ dispatch }, { companyId, colors, existingIds }) {
    try {
      const payload = [
        {
          name: 'Color 1',
          hex: colors.color1,
          companyId,
          id: existingIds?.color1,
        },
        {
          name: 'Color 2',
          hex: colors.color2,
          companyId,
          id: existingIds?.color2,
        },
        {
          name: 'Color 3',
          hex: colors.color3,
          companyId,
          id: existingIds?.color3,
        },
      ];

      const hasExisting = payload.some((p) => p.id);

      if (hasExisting) {
        const updatePromises = payload.map((item) => {
          if (item.id) {
            const { companyId: _, ...itemWithoutCompanyId } = item;
            return dispatch('updateCompanyColor', itemWithoutCompanyId);
          }
          return Promise.resolve();
        });
        await Promise.all(updatePromises);
      } else {
        const createPayload = payload.map(({ id, ...rest }) => rest);
        await dispatch('createCompanyColors', { companyId, colors: createPayload });
      }

      notificationWithIcon('success', 'Company colors saved successfully');
      await dispatch('getCompanyColors', companyId);
    } catch (err) {
      const { response: { data: { message } } = { data: { message: err.message } } } = err;
      notificationWithIcon('error', message);
      throw err;
    }
  },
};

export default actions;
